import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';

import { DemonstrationService } from '@app/configure/services/demonstration.service';
import { SkipFinanceService } from '@app/configure/services/skip-finance.service';
import { ConfigureService } from '@app/configure/services/configure.service';
import { TrackingService } from '@app/core/services/tracking.service';
import { SessionService } from '@app/core/services/session.service';
import { HelperService } from '@app/core/services/helper.service';
import { ModalService } from '@app/core/services/modal.service';
import { ToastService } from '@app/core/services/toast.service';

import { FormInputEvent } from '@app/ui-forms/models/form-input-event.model';
import { FinanceQuote } from '@app/core/models/finance-quote.model';
import { Dealer } from '@app/core/models/dealer.model';

import { FormHelper } from '@app/ui-forms/classes/form-helper';

@Component({
	selector: 'cng-finance-form',
	templateUrl: './finance-form.component.html',
	styleUrls: ['./finance-form.component.less'],
})
export class ConfigureFinanceFormComponent implements OnInit, OnDestroy {
	dealer: Dealer = this.session_service.getDealer();
	loading: boolean = false;
	vehicle: any = this.configure_service.configure_data.steps.purchase_vehicle.data;
	max_deposit_amount: number =
		Math.floor((this.configure_service.configure_data.steps.customise_review.data.grand_total * 0.9) / 100) * 100;
	configure_data: any = this.configure_service.configure_data;
	finance_meta: any = this.configure_service.configure_data.meta.finance;
	finance_quotes_form: FormGroup;
	finance_quotes: FinanceQuote[] = this.configure_service.configure_data.steps.finance_options.data;
	selected_quote: FinanceQuote = this.helper_service.copy(this.finance_quotes).find((quote: FinanceQuote) => {
		return quote.selected;
	});
	finance_column_css_class: string = this.setColumnCssClass();
	lte_sm: boolean = false;
	use_sliders: boolean = true;
	mileage_options: any[] = [];
	deposit_options: any[] = [];
	term_options: any[] = [];
	terms_settings: { min: number, max: number, step: number };
	credit_rating_options: any[] = [];
	credit_rating_display_value: string;
	hasAlteredQuotes: boolean = false;
	tracking_log: string[] = [];
	show_powered_by_logo: boolean = true;
	destroyed$: Subject<boolean> = new Subject<boolean>();
	application_minutes_to_complete?: number;

	constructor(
		private session_service: SessionService,
		public configure_service: ConfigureService,
		private toast_service: ToastService,
		private decimal_pipe: DecimalPipe,
		private modal_service: ModalService,
		private demonstration_service: DemonstrationService,
		private helper_service: HelperService,
		private skip_finance_service: SkipFinanceService,
		private router: Router,
		private tracking_service: TrackingService
	) {}

	ngOnInit(): void {
		this.buildForm();
		this.checkForAlteredValues();
		this.checkScreenWidth();

		this.configure_service.configure_data$.pipe(takeUntil(this.destroyed$)).subscribe((configure_data) => {
			this.finance_meta = configure_data.meta.finance;

			if (!this.finance_meta.enabled) {
				this.toggleFinanceState(false);
			}

			this.max_deposit_amount =
				Math.floor((configure_data.steps.customise_review.data.grand_total * 0.9) / 100) * 100;
			this.finance_quotes = configure_data.steps.finance_options.data;
			this.selected_quote = this.helper_service.copy(this.finance_quotes).find((quote: FinanceQuote) => {
				return quote.selected;
			});

			this.finance_column_css_class = this.setColumnCssClass();

			this.buildForm();
			this.checkForAlteredValues();
		});

		this.application_minutes_to_complete =
			this.configure_data.steps.finance_options.application_minutes_to_complete;

		this.use_sliders = this.helper_service.browserSupports('sliders');

		if (true || !this.use_sliders) {
			this.buildOptions();
		}
	}

	buildForm(): void {
		const credit_score_index: number = this.finance_meta.credit_scores
			.map((cs: any) => {
				return cs.id;
			})
			.indexOf(this.finance_meta.credit_score);

		this.finance_quotes_form = new FormGroup({
			credit_rating: new FormControl(credit_score_index + 1),
			mileage: new FormControl(this.finance_meta.mileage),
			deposit: new FormControl(
				this.finance_meta.deposit > this.max_deposit_amount
					? this.max_deposit_amount
					: this.finance_meta.deposit
			),
			term: new FormControl(this.finance_meta.term),
		});
		this.setCreditRatingDisplayValue();
	}

	setCreditRatingDisplayValue(): void {
		this.credit_rating_display_value = this.getCreditRatingDisplayName();
	}

	calculateTotalDeposit(): number {
		return parseFloat(this.configure_service.getTotalDeposit().toFixed(2));
	}

	onArrangeCallback(): void {
		this.modal_service.show(['configure/callback']);
	}

	buildOptions(): void {
		this.mileage_options = [];
		this.deposit_options = [];
		this.term_options = [];
		this.terms_settings = this.finance_meta.term_settings;

		for (let mileage = 6000; mileage <= 60000; mileage += 1000) {
			this.mileage_options.push({ value: mileage, name: this.decimal_pipe.transform(mileage) });
		}

		for (let deposit = 0; deposit <= this.max_deposit_amount; deposit += 100) {
			this.deposit_options.push({ value: deposit, name: this.decimal_pipe.transform(deposit) });
		}

		for (let term = this.terms_settings.min; term <= this.terms_settings.max; term += this.terms_settings.step) {
			this.term_options.push({ value: term, name: term });
		}

		this.credit_rating_options = this.finance_meta.credit_scores.map((cs: any, index: number) => {
			return { value: index + 1, name: cs.name };
		});
	}

	checkForAlteredValues(): void {
		this.hasAlteredQuotes = false;

		this.finance_quotes = this.finance_quotes.map((quote: FinanceQuote) => {
			quote.deposit_altered =
				quote.total_deposit !== parseFloat(this.finance_quotes_form.controls['deposit'].value);
			quote.annual_mileage_altered =
				quote.annual_mileage !== null &&
				quote.annual_mileage !== parseInt(this.finance_quotes_form.controls['mileage'].value, 10);
			quote.term_altered = quote.term !== parseInt(this.finance_quotes_form.controls['term'].value, 10);

			quote.altered = quote.deposit_altered || quote.annual_mileage_altered || quote.term_altered;

			if (quote.altered) {
				this.hasAlteredQuotes = true;
			}

			return quote;
		});
	}

	onFinanceToggle(type: string): void {
		this.finance_meta.enabled = type !== 'finance';

		if (this.finance_meta.enabled && this.finance_meta.locked) {
			this.modal_service.show(['configure/finance-locked']);
			return;
		}

		this.loading = !this.finance_meta.enabled;
		// this.skip_finance_service.onToggleFinanceState.next(<boolean>!this.finance_meta.enabled);
		this.configure_service.setFinanceEnabled(!this.finance_meta.enabled, false).subscribe((response1) => {
			this.configure_service.saveCustomiseReview(true).subscribe((response2) => {
				if (response1 && response2) {
					this.loading = false;
				}
			});
		});
	}

	onInputChange(input_name: string = null): void {
		if (!!input_name) {
			this.trackSliderChange(input_name);
		}

		this.getQuotes();
	}

	getQuotes(): void {
		if (this.loading) {
			return;
		}

		this.loading = true;

		this.configure_service.configure_data.meta.finance.enabled = true;
		this.configure_service.configure_data.meta.finance.mileage = this.finance_quotes_form.controls['mileage'].value;
		this.configure_service.configure_data.meta.finance.deposit = this.finance_quotes_form.controls['deposit'].value;
		this.configure_service.configure_data.meta.finance.term = this.finance_quotes_form.controls['term'].value;
		this.configure_service.configure_data.meta.finance.credit_score =
			this.finance_meta.credit_scores[this.finance_quotes_form.controls['credit_rating'].value - 1].id;

		this.configure_service.saveCustomiseReview().subscribe(() => {
			this.loading = false;
			this.tracking_service.trackFormSubmitSuccess('finance_quote_form');
			this.toast_service.success('Finance quotes updated');
		});

		this.configure_service
			.track('finance.update', { finance_meta: this.configure_service.configure_data.meta.finance })
			.subscribe();
	}

	setColumnCssClass(): string {
		switch (this.finance_quotes.length) {
			case 1:
				return 'col-xxs-30';
			case 2:
				return 'col-xxs-30 col-sm-15';
			default:
				return 'col-xxs-30 col-sm-15 col-md-10';
		}
	}

	onInfoClick(quote: FinanceQuote, e): void {
		e.stopPropagation();
		this.modal_service.show(['configure/finance-info'], { quote });
	}

	onFinanceAction(type: 'print' | 'apply', quote: FinanceQuote, e): void {
		e.stopPropagation();
		this.configure_service.track('finance.' + type, { quote: quote }).subscribe();
		if (type === 'apply') {
			const route = this.canUseFinanceForm(quote)	? 'configure/finance-form' : 'configure/finance-apply';

			if (route === 'configure/finance-form') {
				this.tracking_service.trackFinanceApplyIntent();
			}

			this.modal_service.show([route], { quote });
		}
	}

	canUseFinanceForm(quote: FinanceQuote): boolean {
		if (this.dealer.finance_module_enabled &&
			((quote.finance_provider_id === 2 && this.dealer.codeweavers_finance_form && this.dealer.finance_api_key) || [3, 8].includes(quote.finance_provider_id))
		) {
			return true;
		}

		return false;
	}

	onFinanceDemonstration(quote: FinanceQuote): void {
		if (quote.selected) {
			this.demonstration_service.previewFinanceUpdate(quote);
		} else {
			this.loading = true;

			this.configure_service.setPreferredFinanceFacilityType(quote.facility_type).subscribe(() => {
				this.loading = false;
				this.demonstration_service.previewFinanceUpdate(quote);
			});
		}
	}

	selectQuote(quote: FinanceQuote): void {
		if (!quote.selected) {
			if (this.finance_meta.locked) {
				this.modal_service.show(['configure/finance-locked']);
				return;
			}

			this.loading = true;

			this.configure_service.setPreferredFinanceFacilityType(quote.facility_type).subscribe(() => {
				this.loading = false;
				this.toast_service.success(quote.facility_type.toUpperCase() + ' Finance Selected');
			});
		}
	}

	getCreditRatingDisplayName(): string {
		if (!this.finance_quotes_form.value.credit_rating) {
			return this.finance_meta.credit_scores[0].name;
		}
		if (this.finance_quotes_form.value.credit_rating > this.finance_meta.credit_scores.length) {
			return this.finance_meta.credit_scores[this.finance_meta.credit_scores.length - 1].name;
		}

		return this.finance_meta.credit_scores[this.finance_quotes_form.value.credit_rating - 1].name;
	}

	unlockDeal(): void {
		this.modal_service.show(['configure', 'finance-locked']);
	}

	onContinue(): void {
		this.configure_service.navigateToStep('delivery_options');
	}

	onReserveNow(): void {
		this.configure_service.navigateToStep('reserve');
	}

	public get showPoweredBy(): boolean {
		return (
			this.finance_meta &&
			this.finance_meta.enabled &&
			this.finance_meta.finance_provider_name &&
			this.finance_quotes !== null &&
			this.finance_quotes.length > 0
		);
	}

	@HostListener('window:resize')
	checkScreenWidth(): void {
		this.lte_sm = this.helper_service.isScreenWidth('<=', 'sm');
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.toggleFinanceState(true);
	}

	protected toggleFinanceState(state: boolean): void {
		this.skip_finance_service.onToggleFinanceState.next(<boolean>state);
	}

	onManageFinanceApplication(): void {
		this.router.navigate(['/finance-manager', this.configure_data.meta.enquiry_hash]);
	}

	onFormInput(form: any): void {
		const event: FormInputEvent = FormHelper.getInputEvent(form);
		if (this.tracking_log.indexOf(event.log_name) === -1) {
			this.tracking_service.trackFormInput(event.form_name, event.input_type, event.input_name);
			this.tracking_log.push(event.log_name);
		}
	}

	trackSliderChange(input_name): void {
		const log_name: string = `finance_quote_form__slider__${input_name}`;
		if (this.tracking_log.indexOf(log_name) === -1) {
			this.tracking_service.trackFormInput('configure_finance_quote_form', 'slider', input_name);
			this.tracking_log.push(log_name);
		}
	}
}
